import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/* GoHighLevel LeadConnector chat widget loader.
 *
 * Strategy:
 *   - Allow-list of public marketing paths (kept narrow per product spec).
 *   - Cold visit to a disallowed route → script is NEVER injected.
 *   - In-session navigation toggles <html data-tme-chat="on|off">; a global
 *     <style> element hides the widget DOM via display:none !important
 *     when "off". This avoids tearing down the GHL widget mid-session
 *     (which the loader doesn't support cleanly) while still respecting
 *     the spec's "hide on /intake and /admin/*" rule.
 *   - Singleton: a window flag + DOM check prevent duplicate <script> tags
 *     across re-renders / React Strict Mode double-invokes / hot reloads.
 *
 * Constants are kept local — no env coupling. Widget ID and source URL
 * come straight from the snippet the owner approved.
 */
const ALLOWED_EXACT_PATHS = new Set([
  "/",
  "/websites",
  "/revenue-systems",
  "/pricing",
  "/packages",
  "/faq",
  "/case-studies",
  "/book",
]);

const SCRIPT_SRC = "https://widgets.leadconnectorhq.com/loader.js";
const RESOURCES_URL =
  "https://widgets.leadconnectorhq.com/chat-widget/loader.js";
const WIDGET_ID = "6a00a4a54c428b3b05321110";
const SOURCE = "WEB_USER";

const SCRIPT_FLAG = "__tmeGhlChatLoaded";
const STYLE_ID = "tme-ghl-chat-style";
const SCRIPT_ID = "tme-ghl-chat-loader";

function isAllowedPath(pathname) {
  if (!pathname) return false;
  // Strict exact-match against the spec'd allow-list. No prefix matching —
  // /admin/* and /intake therefore can never accidentally match.
  return ALLOWED_EXACT_PATHS.has(pathname);
}

function ensureHideStyle() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.setAttribute("data-testid", "tme-ghl-chat-style");
  // Defensive selector — covers known LeadConnector widget DOM patterns.
  // The real widget after hydration is a `<chat-widget>` custom element
  // (not an iframe and without an `lc-` prefix), so it MUST be the first
  // selector. The rest catch loader-stage iframes / lc-captcha / future
  // GHL DOM variations defensively. display:none !important keeps the
  // widget out of layout entirely so mobile CTAs are never covered on
  // /intake or /admin/*.
  style.textContent = `
    html[data-tme-chat="off"] chat-widget,
    html[data-tme-chat="off"] iframe[src*="leadconnectorhq"],
    html[data-tme-chat="off"] iframe[id*="lc-"],
    html[data-tme-chat="off"] iframe[id*="leadconnector" i],
    html[data-tme-chat="off"] [id^="lc-"],
    html[data-tme-chat="off"] [class*="leadconnector" i],
    html[data-tme-chat="off"] [class*="LeadConnector"] {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

function injectScriptOnce() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window[SCRIPT_FLAG]) return;
  // Already injected by us in a previous mount.
  if (document.getElementById(SCRIPT_ID)) {
    window[SCRIPT_FLAG] = true;
    return;
  }
  // Already present statically in index.html (Option A — required by GHL A2P
  // compliance). The static <script> has no id of ours, so detect it by src.
  // When found, just flip the singleton flag and return — DO NOT inject a
  // second loader, which would cause the LC chat-widget bundle to register
  // its custom element twice.
  if (
    document.querySelector(
      'script[src*="widgets.leadconnectorhq.com/loader.js"]',
    )
  ) {
    window[SCRIPT_FLAG] = true;
    return;
  }
  // Runtime fallback: only fires if the static script went missing for any
  // reason (CDN outage, accidental edit to index.html, etc.).
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.src = SCRIPT_SRC;
  s.async = true;
  s.setAttribute("data-resources-url", RESOURCES_URL);
  s.setAttribute("data-widget-id", WIDGET_ID);
  s.setAttribute("data-source", SOURCE);
  s.setAttribute("data-testid", "tme-ghl-chat-script");
  document.body.appendChild(s);
  window[SCRIPT_FLAG] = true;
}

export default function GhlChatWidget() {
  const { pathname } = useLocation();

  useEffect(() => {
    ensureHideStyle();
    const allowed = isAllowedPath(pathname);
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute(
        "data-tme-chat",
        allowed ? "on" : "off",
      );
    }
    if (allowed) {
      injectScriptOnce();
    }
  }, [pathname]);

  return null;
}
